import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import {
  rowAccountKeys,
  snapshotAccounts,
  venueAccountKey,
  type OpenerDescalceAccountMeta,
  type OpenerDescalceRow
} from '../../api/useOpenerDescalce';
import { hasUnreadLeg } from './descalceLegs';

/** Client-side filter state applied over the already-fetched snapshot. */
export interface DescalceFilterState {
  search: string;
  minDescalce: number;
  /**
   * Only rows whose group has `podsDesired: 'on'`. ON by default — and it also
   * takes out PANICKED groups and UNGROUPED rows (the core engine's), which is
   * why the header strip counts what THIS toggle alone suppresses
   * ({@link countGroupsOnHidden}).
   */
  onlyGroupsOn: boolean;

  /**
   * Venue-account keys (`platform:accountId`) the operator has selected. The
   * vocabulary is whatever the snapshot carries — never a fixed list, since
   * the fleet is data now. EMPTY means no account scoping at all (every row),
   * so a snapshot whose accounts the FE has never heard of still renders.
   */
  accounts: string[];
  onlyDivergence: boolean;
  /** Only rows carrying an unread triage card. OFF by default. */
  onlyWithTriage: boolean;
}

/**
 * The triage marks live in the triage index, not on the snapshot row, so the
 * lookup is injected. Absent, `onlyWithTriage` hides every row rather than
 * quietly passing them all: a filter that cannot be evaluated must not present
 * an unfiltered list as a filtered one.
 */
export interface DescalceTriageLookup {
  hasTriage?: (row: OpenerDescalceRow) => boolean;
}

/** The selectable account keys, in the snapshot's own order. */
export function accountFilterOptions(
  accounts: OpenerDescalceAccountMeta[] | undefined
): { key: string; account: OpenerDescalceAccountMeta }[] {
  return snapshotAccounts({ accounts: accounts as OpenerDescalceAccountMeta[] }).map(
    account => ({
      key: venueAccountKey(account.platform, account.accountId),
      account
    })
  );
}

/**
 * Drop selected keys the snapshot no longer offers. The filter bar only
 * renders a toggle for a key the snapshot carries, so a selection that
 * outlives its account becomes un-clickable while still hiding every row —
 * the operator sees "0 rows" with no visible cause.
 */
export function pruneAccountFilter(
  selected: string[],
  accounts: OpenerDescalceAccountMeta[] | undefined
): string[] {
  const offered = new Set(accountFilterOptions(accounts).map(o => o.key));
  return selected.filter(key => offered.has(key));
}

/** Toggle one account key in/out of the selection. */
export function toggleAccountFilter(
  selected: string[],
  key: string
): string[] {
  return selected.includes(key)
    ? selected.filter(k => k !== key)
    : [...selected, key];
}

/**
 * The screen opens on the LIVE view: groups whose pods are ON. A resolved leg
 * is not a filter at all any more — the sweeper does not publish those rows
 * (see {@link dropResolvedRows}), because a leg that already resolved at its
 * venue cannot be hedged against and is therefore not what this screen is for.
 */
export const DEFAULT_DESCALCE_FILTERS: DescalceFilterState = {
  search: '',
  minDescalce: 0.5,
  onlyGroupsOn: true,
  accounts: [],
  onlyDivergence: false,
  onlyWithTriage: false
};

/**
 * A row is kept when EITHER of its two venue-accounts is selected. A row spans
 * one Kalshi and one Polymarket account, so requiring both would make a
 * single-account selection match nothing — and the operator scoping to one
 * account wants every row that touches it.
 */
function matchesAccounts(row: OpenerDescalceRow, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return rowAccountKeys(row).some(key => selected.includes(key));
}

function matchesSearch(row: OpenerDescalceRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = `${row.pairName ?? ''} ${row.group.name ?? ''}`.toLowerCase();
  return haystack.includes(q);
}

/**
 * Two kinds of row are never hidden behind the numeric floor, because for
 * neither is the number a real cross-venue descalce to threshold:
 *  - `venue === null` (unconfigured / starting / error account) — exactly the
 *    "unknown" the screen exists to surface.
 *  - `descalce === null` (a leg went unread) — there is no number to compare
 *    against a floor, and dropping the row would silently retire the very
 *    rules whose exposure is unknown.
 *  - `marketsIncomplete` — the backend emits these with counts it deliberately
 *    refused to attribute. A rule whose two Polymarket tokens collide has both
 *    Poly sides forced to `0` while real size is held, so it arrives at
 *    `descalce: 0` BY CONSTRUCTION and the floor would delete the one row the
 *    backend went out of its way to keep visible.
 */
function matchesMinDescalce(row: OpenerDescalceRow, min: number): boolean {
  if (min <= 0) return true;
  if (!row.venue) return true;
  if (row.venue.descalce == null || hasUnreadLeg(row)) return true;
  if (row.venue.marketsIncomplete === true) return true;
  return row.venue.descalce >= min;
}

/**
 * A divergence is the venue read MINUS the engine belief. Where there is no
 * venue read — an unreadable account, or a leg the sweep never reached — the
 * difference is UNKNOWN, not zero, and a filter for "belief disagrees with
 * reality" must not retire exactly the rows on which the disagreement cannot
 * be measured. Those are kept.
 *
 * A `null` divergence on a fully-read row is a different claim: the engine has
 * simply published no belief for that rule (engine off, or restarted). Nothing
 * disagrees with anything there, so the filter drops it as before.
 */
function matchesDivergence(row: OpenerDescalceRow): boolean {
  if (!row.venue || hasUnreadLeg(row)) return true;
  if (row.divergence == null) return false;
  return Math.abs(row.divergence.total) > IMBALANCE_TOLERANCE;
}

export function applyDescalceFilters(
  rows: readonly OpenerDescalceRow[],
  filters: DescalceFilterState,
  triage?: DescalceTriageLookup
): OpenerDescalceRow[] {
  return rows.filter(row => {
    if (!matchesSearch(row, filters.search)) return false;
    if (!matchesMinDescalce(row, filters.minDescalce)) return false;
    if (filters.onlyGroupsOn && row.group.podsDesired !== 'on') return false;
    if (!matchesAccounts(row, filters.accounts)) return false;
    if (filters.onlyDivergence && !matchesDivergence(row)) return false;
    if (filters.onlyWithTriage && triage?.hasTriage?.(row) !== true) return false;
    return true;
  });
}

/**
 * Split off the rows whose leg already resolved at its venue.
 *
 * The sweeper stops publishing these (it counts them into
 * `snapshot.resolvedExcluded` instead), so in steady state `resolved` is
 * empty. It is applied here ANYWAY and unconditionally, for the two states
 * where the wire still carries them: a backend older than that change, during
 * the deploy window, and a snapshot retained from before it. One rule — a
 * resolved leg is not on this screen — enforced at both ends.
 *
 * Nothing is hidden silently: the header strip adds `resolved.length` to the
 * backend's own count and renders the total.
 */
export function dropResolvedRows(rows: readonly OpenerDescalceRow[]): {
  live: OpenerDescalceRow[];
  resolved: OpenerDescalceRow[];
} {
  const live: OpenerDescalceRow[] = [];
  const resolved: OpenerDescalceRow[] = [];
  for (const row of rows) {
    if (row.venue?.resolving === true) resolved.push(row);
    else live.push(row);
  }
  return { live, resolved };
}

/**
 * How many rows `onlyGroupsOn` ALONE is suppressing — rows that clear every
 * other active filter and are hidden only because their group is stopped,
 * PANICKED, or does not exist (an ungrouped row, which has no group anybody
 * could turn on). `0` when the toggle is off.
 *
 * 🚨 Deliberately NOT "live rows minus visible rows". That delta is dominated
 * by the numeric floor — every fully hedged pair is published and sits under
 * it — so it would report hundreds in a healthy fleet and bury the two or
 * three rows this count exists to surface, under a tooltip blaming a filter
 * that hid almost none of them.
 */
export function countGroupsOnHidden(
  rows: readonly OpenerDescalceRow[],
  filters: DescalceFilterState,
  triage?: DescalceTriageLookup
): number {
  if (!filters.onlyGroupsOn) return 0;
  return applyDescalceFilters(
    rows,
    { ...filters, onlyGroupsOn: false },
    triage
  ).filter(row => row.group.podsDesired !== 'on').length;
}

export interface DescalceTotals {
  rowCount: number;
  /** Sum of `venue.descalce` over NON-ambiguous, fully-read rows only. */
  totalDescalce: number;
  ambiguousCount: number;
  /**
   * Rows left OUT of `totalDescalce` because their position could not be read
   * — a leg the sweep never reached, or a whole venue account that could not
   * be read at all (`venue === null`, i.e. an `error` / `unconfigured` /
   * `starting` account). Both are the same shortfall to the operator.
   */
  unreadCount: number;
  /** Visible rows carrying an unread counter-stuck / orphaned-fill card. */
  triageCount: number;
}

/**
 * Header-strip totals. Two kinds of row are excluded from the summed total:
 *  - ambiguous ones — each claimant carries the FULL market size (never an
 *    invented split, per the sweeper's own invariant), so summing them in
 *    would double (or N-fold) count contracts on a single venue position;
 *  - rows with an unread leg, and rows whose venue accounts could not be read
 *    at all — there is no number to add, and adding `0` would report an
 *    unknown as hedged. They are counted separately instead, so the total is
 *    never quietly short.
 */
export function computeDescalceTotals(
  rows: readonly OpenerDescalceRow[],
  triage?: DescalceTriageLookup
): DescalceTotals {
  let totalDescalce = 0;
  let ambiguousCount = 0;
  let unreadCount = 0;
  let triageCount = 0;
  for (const row of rows) {
    // Counted BEFORE the unread guard below: a row whose account is unreadable
    // can still be the one holding an open triage card.
    if (triage?.hasTriage?.(row) === true) triageCount++;
    // A missing `venue` block is the strongest form of "not read": the row's
    // accounts themselves are unreadable. It belongs in `unreadCount` with the
    // half-read rows, so the shortfall in the total always has a row count
    // beside it. Strictly stronger than main's bare `!row.venue` guard, which
    // it subsumes.
    if (!row.venue || row.venue.descalce == null || hasUnreadLeg(row)) {
      unreadCount++;
      continue;
    }
    if (row.venue.ambiguous) {
      ambiguousCount++;
      continue;
    }
    totalDescalce += row.venue.descalce;
  }
  return {
    rowCount: rows.length,
    totalDescalce,
    ambiguousCount,
    unreadCount,
    triageCount
  };
}
